// ============================================================================
//  useFeatureStatus — polls /api/booth/360/{booth_id}/feature-status
//  and fires an onPromoted callback the FIRST time the booth wins a
//  category (the not-featured → featured transition).
//
//  Per the user's brief: the live promotion toast must appear
//  ONCE — when the spin first lands on the wall — not on every poll.
//  The hook owns that transition detection so callers don't have to.
//
//  Polling cadence:
//    • 8s for the first 60s (the high-leverage window — the spin's
//      just been created, organic shares are happening NOW)
//    • 30s after that — keeps the badge fresh without hammering Mongo
//
//  Returns: { featured, categories, loaded }
// ============================================================================
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAST_POLL_MS = 8000;
const SLOW_POLL_MS = 30000;
const FAST_POLL_WINDOW_MS = 60000;

export const useFeatureStatus = (boothId, onPromoted) => {
  const [featured, setFeatured] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const wasFeaturedRef = useRef(false);
  const onPromotedRef = useRef(onPromoted);
  // Keep the callback ref fresh without re-running the polling effect
  // every time the parent re-renders with a new closure.
  useEffect(() => { onPromotedRef.current = onPromoted; }, [onPromoted]);

  useEffect(() => {
    if (!boothId) return undefined;
    let cancelled = false;
    const startedAt = Date.now();
    let timer = null;

    const tick = async () => {
      try {
        const r = await axios.get(`${API}/booth/360/${boothId}/feature-status`);
        if (cancelled) return;
        const isFeatured = !!r.data?.featured;
        const cats = r.data?.categories || [];
        setFeatured(isFeatured);
        setCategories(cats);
        setLoaded(true);
        // ── Transition detection: not-featured → featured ──
        // This is the moment the toast fires. Sticky after the first
        // fire so we don't spam if the booth flickers in/out (e.g.,
        // someone else stole Trending Tonight 60s later — the toast
        // should not re-fire on re-entry; the badge handles "still
        // featured" affordance).
        if (!wasFeaturedRef.current && isFeatured && cats.length > 0) {
          wasFeaturedRef.current = true;
          try { onPromotedRef.current?.(cats); } catch { /* ignore */ }
        }
      } catch { /* network/4xx — leave previous state; try again on next tick */ }
      finally {
        if (cancelled) return;
        const elapsed = Date.now() - startedAt;
        const next = elapsed < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;
        timer = setTimeout(tick, next);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [boothId]);

  return { featured, categories, loaded };
};

export default useFeatureStatus;
